import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, Calendar, User, MessageSquare, Image as ImageIcon, AlertCircle, Send,
  CheckCircle, Mail, FileText, Clock, ChevronRight, Layers, Edit3, Eye,
  Sparkles, Camera, Loader2, Trash2, ShieldCheck, Languages, Download,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";
import { usePortalLang } from "../../lib/PortalLanguageContext";
import { AutomationCard } from "./AutomationCard";
import DOMPurify from "dompurify";

/* ---------- Shared helpers ---------- */

const ROLE_LABEL = {
  admin: "Main Admin", project_manager: "Project Manager", admin_staff: "Admin Staff",
  site_engineer: "Site Engineer", client: "Client", consultant: "Consultant",
  subcontractor: "Subcontractor", supplier: "Supplier",
  client_project_manager: "Client PM", client_interior_designer: "Client Designer",
  client_associate: "Client Associate", client_subcontractor: "Client Sub",
  client_supplier: "Client Supplier", delivery_team: "Delivery", housekeeping_team: "Housekeeping",
};

const VIS_BADGE = {
  public: "bg-[#3F6E2C] text-white",
  client: "bg-[#D4A963] text-[#1C110C]",
  subcontractor: "bg-[#56151F] text-white",
  internal: "bg-[#1C110C] text-white",
};
const VIS_SHORT = {
  public: "Public", client: "Client", subcontractor: "Subs", internal: "Internal",
};

function initials(name = "") {
  return name
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "·";
}

/* ---------- Lightbox ---------- */

export function PhotoLightbox({ photo, onClose, onPrev, onNext, hasPrev, hasNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);
  if (!photo) return null;
  return (
    <div
      data-testid="portal-photo-lightbox"
      className="fixed inset-0 z-[60] bg-[#1C110C]/92 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="portal-photo-lightbox-close"
        className="absolute top-4 right-4 text-[#FAF5EC] hover:text-[#D4A963] p-2"
      >
        <X size={22} />
      </button>
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 text-[#FAF5EC] hover:text-[#D4A963] text-3xl p-3"
          aria-label="Previous photo"
        >
          ‹
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 text-[#FAF5EC] hover:text-[#D4A963] text-3xl p-3"
          aria-label="Next photo"
        >
          ›
        </button>
      )}
      <figure
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-4"
      >
        <img
          src={photo.url || portalApi.attachmentUrl(photo.id)}
          alt={photo.caption || ""}
          className="max-w-full max-h-[78vh] object-contain shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
        />
        <figcaption className="bg-[#FAF5EC] text-[#1C110C] px-5 py-3 max-w-[90vw] w-full md:w-auto">
          {photo.caption && (
            <p data-testid="portal-photo-lightbox-caption" className="font-serif-display text-base text-[#1C110C] mb-1.5">{photo.caption}</p>
          )}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">
                {photo.date || "—"}
                {photo.uploader ? ` · ${photo.uploader}` : ""}
                {photo.role ? ` · ${ROLE_LABEL[photo.role] || photo.role}` : ""}
              </p>
              {photo.linked_label && (
                <p className="text-xs text-[#3E2A22] mt-1 truncate"><Layers className="inline mr-1" size={11} />{photo.linked_label}</p>
              )}
            </div>
            {photo.visibility && (
              <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${VIS_BADGE[photo.visibility] || "bg-[#7A6B5F] text-white"}`}>
                {VIS_SHORT[photo.visibility] || photo.visibility}
              </span>
            )}
          </div>
        </figcaption>
      </figure>
    </div>
  );
}

/* ---------- Daily Update Card ---------- */

function LinkedTaskChip({ update, timeline }) {
  if (!timeline && !update.linked_milestone_name && !update.linked_package_name) return null;
  const task = update.task_id && timeline?.tasks?.find((t) => t.id === update.task_id);
  const phase = update.phase_id && timeline?.phases?.find((p) => p.id === update.phase_id);
  const milestoneName = update.linked_milestone_name
    || (update.milestone_id && timeline?.milestones?.find((m) => m.id === update.milestone_id)?.name)
    || (update.milestone_id ? "Milestone removed" : null);
  if (!task && !phase && !milestoneName && !update.subcontractor_scope && !update.linked_package_name) return null;
  const label = [phase?.name, task?.name, update.subcontractor_scope].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-wrap items-center gap-2">
      {label && (
        <div
          data-testid={`portal-update-linked-${update.id}`}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase bg-[#FAF5EC] border border-[#D4A963]/40 text-[#6E1F2C] px-2.5 py-1"
        >
          <Layers size={10} /> {label}
        </div>
      )}
      {milestoneName && (
        <div
          data-testid={`portal-update-linked-milestone-${update.id}`}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase bg-[#D4A963]/15 border border-[#D4A963] text-[#A6822F] px-2.5 py-1"
          title={`Linked milestone: ${milestoneName}`}
        >
          <span className="text-[#A6822F]">★</span> {milestoneName}
        </div>
      )}
      {update.linked_package_name && (
        <div
          data-testid={`portal-update-linked-package-${update.id}`}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase bg-[#56151F]/10 border border-[#56151F]/40 text-[#56151F] px-2.5 py-1"
          title={`Linked vendor package: ${update.linked_package_name}`}
        >
          <Layers size={10} /> {update.linked_package_name}
        </div>
      )}
    </div>
  );
}

function StatPill({ icon: Icon, label, value, accent = "default" }) {
  const accents = {
    default: "bg-[#FAF5EC] text-[#3E2A22] border-[#6E1F2C]/15",
    risk: "bg-[#A3141A]/8 text-[#A3141A] border-[#A3141A]/30",
    gold: "bg-[#D4A963]/15 text-[#6E1F2C] border-[#D4A963]/40",
    success: "bg-[#3F6E2C]/10 text-[#2E5320] border-[#3F6E2C]/30",
  };
  return (
    <div className={`flex items-start gap-2 border ${accents[accent] || accents.default} px-3 py-2`}>
      <Icon size={12} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[9px] tracking-[0.22em] uppercase opacity-70">{label}</p>
        <p className="text-xs leading-tight mt-0.5 line-clamp-2">{value}</p>
      </div>
    </div>
  );
}

export function TranslatedText({ original, translated, className }) {
  const [showOrig, setShowOrig] = useState(false);
  if (!translated || translated === original) {
    return <p className={className}>{original}</p>;
  }
  return (
    <div>
      <p className={className}>{showOrig ? original : translated}</p>
      <button
        type="button"
        onClick={() => setShowOrig((v) => !v)}
        data-testid="portal-translate-toggle"
        className="mt-1 inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase text-[#A6822F] hover:text-[#6E1F2C] transition-colors"
      >
        <Languages size={10} /> {showOrig ? "Show translation" : "Auto-translated · View original"}
      </button>
    </div>
  );
}

export function DailyUpdateCard({ update, timeline, onOpenPhoto, onComment, canEdit, onEdit, tmap, commentCount }) {
  const u = update;
  const tr = (field, orig) => (tmap && tmap[`upd:${u.id}:${field}`]) || orig;
  const stats = [];
  if (u.manpower != null) stats.push({ icon: User, label: "Manpower", value: u.manpower });
  if (u.subs_on_site) stats.push({ icon: User, label: "Subs on site", value: u.subs_on_site });
  if (u.materials_delivered) stats.push({ icon: Sparkles, label: "Materials", value: tr("materials", u.materials_delivered) });
  if (u.delays) stats.push({ icon: AlertCircle, label: "Delays", value: tr("delays", u.delays), accent: "risk" });
  if (u.next_day_plan) stats.push({ icon: Clock, label: "Next day", value: tr("plan", u.next_day_plan), accent: "gold" });
  if (u.progress_percent != null) stats.push({ icon: CheckCircle, label: "Progress", value: `${u.progress_percent}%`, accent: "success" });

  return (
    <article
      data-testid={`portal-update-card-${u.id}`}
      className="bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/30 hover:shadow-[0_10px_30px_-15px_rgba(110,31,44,0.25)] transition-all duration-300 overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-[#6E1F2C]/10 flex items-start justify-between gap-3 flex-wrap bg-gradient-to-r from-white to-[#FAF5EC]/60">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-[#6E1F2C] text-[#FAF5EC] flex items-center justify-center text-xs font-medium shrink-0">
            {initials(u.author?.name)}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-[#1C110C] font-medium truncate">{u.author?.name || "—"}</p>
            <p className="text-[10px] text-[#7A6B5F] tracking-[0.16em] uppercase">
              {ROLE_LABEL[u.author?.role] || u.author?.role || "team"}
              {u.author?.company ? ` · ${u.author.company}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-1">
            <Calendar size={11} /> {u.date}
          </span>
          {u.visibility && (
            <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-1 ${VIS_BADGE[u.visibility] || "bg-[#7A6B5F] text-white"}`}>
              {VIS_SHORT[u.visibility] || u.visibility}
            </span>
          )}
        </div>
      </div>

      <div className="px-5 py-5">
        {(u.task_id || u.phase_id || u.milestone_id || u.subcontractor_scope || u.linked_package_name) && (
          <div className="mb-3">
            <LinkedTaskChip update={u} timeline={timeline} />
          </div>
        )}

        <TranslatedText
          original={u.work_completed}
          translated={tmap ? tmap[`upd:${u.id}:work`] : null}
          className="text-sm md:text-[15px] text-[#1C110C] whitespace-pre-wrap leading-relaxed"
        />

        {stats.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {stats.map((s) => (
              <StatPill key={s.label} {...s} />
            ))}
          </div>
        )}

        {u.photo_ids && u.photo_ids.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] mb-2 inline-flex items-center gap-1.5">
              <ImageIcon size={11} /> {u.photo_ids.length} photo{u.photo_ids.length === 1 ? "" : "s"}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {u.photo_ids.map((pid, idx) => {
                const url = u.photo_urls?.[idx] || portalApi.attachmentUrl(pid);
                const caption = u.photo_captions?.[idx];
                return (
                  <div key={pid} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => onOpenPhoto && onOpenPhoto({
                        id: pid, url, caption, date: u.date, uploader: u.author?.name,
                        role: u.author?.role, visibility: u.visibility,
                      })}
                      data-testid={`portal-update-photo-${u.id}-${pid}`}
                      className="group block w-full aspect-square overflow-hidden bg-[#FAF5EC] border border-[#6E1F2C]/15 hover:border-[#6E1F2C] transition-all"
                    >
                      <img
                        src={url}
                        alt={caption || ""}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </button>
                    {caption && (
                      <p
                        data-testid={`portal-update-caption-${u.id}-${pid}`}
                        className="text-[11px] text-[#3E2A22] leading-snug line-clamp-2 px-0.5"
                      >
                        {caption}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {u.files && u.files.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] mb-2 inline-flex items-center gap-1.5">
              <FileText size={11} /> {u.files.length} attachment{u.files.length === 1 ? "" : "s"}
            </p>
            <ul className="flex flex-wrap gap-2" data-testid={`portal-update-files-${u.id}`}>
              {u.files.map((f) => (
                <li key={f.id}>
                  <a
                    href={f.url || portalApi.attachmentUrl(f.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={f.filename}
                    data-testid={`portal-update-file-${u.id}-${f.id}`}
                    className="inline-flex items-center gap-2 bg-[#FAF5EC] border border-[#6E1F2C]/20 hover:border-[#6E1F2C] px-3 py-1.5 text-xs text-[#3E2A22]"
                  >
                    <FileText size={12} className="text-[#6E1F2C]" />
                    <span className="max-w-[200px] truncate">{f.filename}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(onComment || (canEdit && onEdit)) && (
          <div className="mt-4 pt-3 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2 flex-wrap">
            {canEdit && onEdit && (
              <button
                type="button"
                onClick={() => onEdit(u)}
                data-testid={`portal-update-edit-btn-${u.id}`}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] hover:text-[#56151F] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-1.5"
              >
                <Edit3 size={11} /> Edit
              </button>
            )}
            {onComment && (
              <button
                type="button"
                onClick={() => onComment(u)}
                data-testid={`portal-update-comment-btn-${u.id}`}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] hover:text-[#56151F] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-1.5"
              >
                <MessageSquare size={11} /> Comment{typeof commentCount === "number" && commentCount > 0 ? ` (${commentCount})` : ""}
              </button>
            )}
            {update?.edited_at && (
              <span
                className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]"
                data-testid={`portal-update-edited-tag-${u.id}`}
                title={`Edited ${update.edited_at}`}
              >
                Edited
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/* ---------- Photos Tab ---------- */

function dateBucket(dateStr) {
  if (!dateStr) return "Earlier";
  try {
    const d = new Date(dateStr.slice(0, 10));
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const diff = Math.round((now - d) / 86400000);
    if (diff <= 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff <= 7) return "This week";
    if (diff <= 30) return "This month";
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
  } catch {
    return "Earlier";
  }
}

export function PhotosTab({ updates, timeline, currentUser, onCaptionSaved }) {
  const [active, setActive] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingId, setSavingId] = useState(null);
  const allPhotos = useMemo(() => {
    const arr = [];
    const seen = new Set();
    (updates || []).forEach((u) => {
      (u.photo_ids || []).forEach((pid, idx) => {
        if (!pid || seen.has(pid)) return;
        seen.add(pid);
        const task = u.task_id && timeline?.tasks?.find((t) => t.id === u.task_id);
        const phase = u.phase_id && timeline?.phases?.find((p) => p.id === u.phase_id);
        arr.push({
          id: pid,
          url: u.photo_urls?.[idx] || portalApi.attachmentUrl(pid),
          caption: u.photo_captions?.[idx] || null,
          uploader_id: u.author_id,
          date: u.date,
          uploader: u.author?.name,
          role: u.author?.role,
          visibility: u.visibility,
          linked_label: [phase?.name, task?.name, u.subcontractor_scope].filter(Boolean).join(" · ") || null,
        });
      });
    });
    // Sort newest first
    return arr.sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [updates, timeline]);

  const groups = useMemo(() => {
    const out = {};
    const order = [];
    allPhotos.forEach((p) => {
      const key = dateBucket(p.date);
      if (!out[key]) { out[key] = []; order.push(key); }
      out[key].push(p);
    });
    return order.map((k) => ({ key: k, photos: out[k] }));
  }, [allPhotos]);

  if (!allPhotos.length) {
    return (
      <div
        data-testid="portal-photos-empty"
        className="bg-white border border-dashed border-[#6E1F2C]/25 p-12 text-center"
      >
        <ImageIcon className="mx-auto text-[#D4A963] mb-3" size={36} />
        <p className="text-sm text-[#3E2A22] max-w-md mx-auto">
          No photos uploaded yet. Site photos posted with daily updates will appear here.
        </p>
      </div>
    );
  }

  const idx = active ? allPhotos.findIndex((p) => p.id === active.id) : -1;
  return (
    <div data-testid="portal-photos-tab" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Gallery</p>
          <h2 className="font-serif-display text-2xl text-[#1C110C]">Project photos</h2>
        </div>
        <p className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F]">
          {allPhotos.length} photo{allPhotos.length === 1 ? "" : "s"} · grouped by date
        </p>
      </div>

      {groups.map((g) => (
        <section key={g.key} data-testid={`portal-photos-group-${g.key.replace(/\s+/g, '-').toLowerCase()}`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px flex-1 bg-[#D4A963]/30" />
            <p className="text-[10px] tracking-[0.36em] uppercase text-[#6E1F2C]">{g.key}</p>
            <span className="h-px flex-1 bg-[#D4A963]/30" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {g.photos.map((p) => {
              const canEdit = currentUser && (
                ["admin", "project_manager"].includes(currentUser.role) ||
                (currentUser.role === "site_engineer" && currentUser.id === p.uploader_id)
              );
              const isEditing = editingId === p.id;
              const saveCaption = async () => {
                setSavingId(p.id);
                try {
                  await portalApi.updatePhotoCaption(p.id, editText);
                  setEditingId(null);
                  if (onCaptionSaved) onCaptionSaved();
                } catch { /* ignore */ }
                finally { setSavingId(null); }
              };
              return (
                <div key={p.id} className="space-y-1.5">
                  <button
                    type="button"
                    onClick={() => setActive(p)}
                    data-testid={`portal-photo-thumb-${p.id}`}
                    className="group relative aspect-square w-full bg-[#FAF5EC] border border-[#6E1F2C]/15 hover:border-[#6E1F2C] overflow-hidden text-left transition-all"
                  >
                    <img
                      src={p.url || portalApi.attachmentUrl(p.id)}
                      alt={p.caption || ""}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {p.visibility && (
                      <span className={`absolute top-2 right-2 text-[8px] tracking-[0.18em] uppercase px-1.5 py-0.5 ${VIS_BADGE[p.visibility] || "bg-[#7A6B5F] text-white"}`}>
                        {VIS_SHORT[p.visibility] || p.visibility}
                      </span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1C110C]/90 via-[#1C110C]/40 to-transparent text-[#FAF5EC] p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[10px] tracking-wider truncate">{p.uploader || "—"}</p>
                      {p.linked_label && <p className="text-[9px] opacity-80 truncate">{p.linked_label}</p>}
                    </div>
                  </button>
                  {isEditing ? (
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveCaption(); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                        maxLength={280}
                        placeholder="Short caption…"
                        data-testid={`portal-photo-caption-input-${p.id}`}
                        className="flex-1 min-w-0 bg-white border border-[#6E1F2C]/30 px-2 py-1 text-[11px] text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                      />
                      <button
                        type="button"
                        onClick={saveCaption}
                        disabled={savingId === p.id}
                        data-testid={`portal-photo-caption-save-${p.id}`}
                        className="bg-[#6E1F2C] text-[#FAF5EC] px-2 text-[10px] tracking-wider uppercase disabled:opacity-60"
                      >
                        {savingId === p.id ? "…" : "Save"}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-1.5 min-h-[1.5rem]">
                      {p.caption ? (
                        <p
                          data-testid={`portal-photo-caption-${p.id}`}
                          className="text-[11px] text-[#3E2A22] leading-snug line-clamp-2 flex-1"
                        >
                          {p.caption}
                        </p>
                      ) : (
                        <span className="text-[10px] text-[#7A6B5F] italic flex-1">No caption</span>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => { setEditingId(p.id); setEditText(p.caption || ""); }}
                          data-testid={`portal-photo-caption-edit-${p.id}`}
                          className="text-[#6E1F2C] hover:text-[#56151F] shrink-0"
                          title="Edit caption"
                        >
                          <Edit3 size={11} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <PhotoLightbox
        photo={active}
        onClose={() => setActive(null)}
        hasPrev={idx > 0}
        hasNext={idx >= 0 && idx < allPhotos.length - 1}
        onPrev={() => setActive(allPhotos[idx - 1])}
        onNext={() => setActive(allPhotos[idx + 1])}
      />
    </div>
  );
}

/* ---------- Reports Tab ---------- */

const REPORT_STATUS = {
  draft: { label: "Draft", cls: "bg-[#7A6B5F] text-white" },
  sent: { label: "Sent", cls: "bg-[#3F6E2C] text-white" },
  approved: { label: "Approved", cls: "bg-[#6E1F2C] text-white" },
  acknowledged: { label: "Acknowledged", cls: "bg-[#D4A963] text-[#1C110C]" },
};

function ReportCard({ r, onOpen, canManage }) {
  const status = REPORT_STATUS[r.status] || { label: r.status, cls: "bg-[#7A6B5F] text-white" };
  const recipients = r.recipients || [];
  const range = r.kind === "weekly" && r.period_end ? `${r.period_start} → ${r.period_end}` : r.period_start;
  return (
    <button
      type="button"
      onClick={() => onOpen(r)}
      data-testid={`portal-report-card-${r.id}`}
      className="group relative text-left bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/45 hover:shadow-[0_8px_24px_-10px_rgba(110,31,44,0.25)] transition-all overflow-hidden"
    >
      <div className={`absolute top-0 left-0 right-0 h-[3px] ${r.kind === "daily" ? "bg-[#6E1F2C]" : "bg-[#D4A963]"}`} />
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] font-medium">
            <FileText size={11} /> {r.kind === "daily" ? "Daily" : "Weekly"}
          </span>
          <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-1 ${status.cls}`}>
            {status.label}
          </span>
        </div>
        <h3 className="font-serif-display text-base text-[#1C110C] group-hover:text-[#6E1F2C] line-clamp-2">
          {r.title || `${r.kind === "daily" ? "Daily" : "Weekly"} report`}
        </h3>
        <p className="text-[11px] text-[#7A6B5F] tracking-wide mt-1.5 inline-flex items-center gap-1.5">
          <Calendar size={10} /> {range || "—"}
        </p>

        <div className="mt-4 pt-3 border-t border-[#6E1F2C]/10 space-y-2">
          {recipients.length > 0 ? (
            <div>
              <p className="text-[9px] tracking-[0.26em] uppercase text-[#7A6B5F] mb-1.5 inline-flex items-center gap-1">
                <Mail size={10} /> Recipients
              </p>
              <div className="flex flex-wrap gap-1">
                {recipients.slice(0, 3).map((e, i) => (
                  <span key={i} className="text-[10px] bg-[#FAF5EC] border border-[#6E1F2C]/15 text-[#3E2A22] px-2 py-0.5 max-w-[180px] truncate">
                    {e}
                  </span>
                ))}
                {recipients.length > 3 && (
                  <span className="text-[10px] text-[#7A6B5F] px-2 py-0.5">+{recipients.length - 3}</span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-[10px] text-[#7A6B5F] italic">No recipients yet</p>
          )}
          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
            {r.sent_at ? (
              <p className="text-[10px] text-[#3F6E2C] inline-flex items-center gap-1">
                <CheckCircle size={10} /> Sent {new Date(r.sent_at).toLocaleDateString()}
              </p>
            ) : (
              <p className="text-[10px] text-[#7A6B5F]">Not sent</p>
            )}
            {r.response_count > 0 ? (
              <p className="text-[10px] text-[#6E1F2C] inline-flex items-center gap-1">
                <MessageSquare size={10} /> {r.response_count} response{r.response_count === 1 ? "" : "s"}
              </p>
            ) : r.status === "sent" && r.response_type === "approval_required" ? (
              <p className="text-[10px] text-[#A6822F] inline-flex items-center gap-1" data-testid={`portal-report-awaiting-approval-${r.id}`}>
                <Clock size={10} /> Awaiting approval
              </p>
            ) : r.status === "sent" && r.response_type === "response_required" ? (
              <p className="text-[10px] text-[#D4A963] inline-flex items-center gap-1" data-testid={`portal-report-awaiting-reply-${r.id}`}>
                <Clock size={10} /> Awaiting reply
              </p>
            ) : r.status === "sent" ? (
              <p className="text-[10px] text-[#7A6B5F] inline-flex items-center gap-1" data-testid={`portal-report-info-only-${r.id}`}>
                <CheckCircle size={10} /> Information only
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] group-hover:text-[#56151F]">
            {canManage ? <Edit3 size={11} /> : <Eye size={11} />} {canManage ? "Open & edit" : "Preview"} <ChevronRight size={12} />
          </span>
        </div>
      </div>
    </button>
  );
}

function ReportDetailModal({ slug, report, onClose, canManage, onChanged }) {
  const reportId = report.id;
  const [r, setR] = useState(report);
  const [responses, setResponses] = useState([]);
  const [memberChecks, setMemberChecks] = useState({}); // {email:true|false}
  const [members, setMembers] = useState([]);
  const [extras, setExtras] = useState([]);
  const [configured, setConfigured] = useState([]);
  const [additional, setAdditional] = useState("");
  const [responseText, setResponseText] = useState("");
  const [respType, setRespType] = useState("none");
  const [requiredFrom, setRequiredFrom] = useState("client");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [pdfLinks, setPdfLinks] = useState({});
  const [shareCopied, setShareCopied] = useState(false);

  // Reload report detail whenever slug/report.id changes.
  useEffect(() => {
    let alive = true;
    portalApi.getReport(slug, reportId).then((d) => {
      if (!alive) return;
      setR(d.report);
      setResponses(d.responses || []);
      setPdfLinks({ pdf_url: d.pdf_url, pdf_share_url: d.pdf_share_url });
      if (d.report.response_type) setRespType(d.report.response_type);
      if (d.report.required_from) setRequiredFrom(d.report.required_from);
      const existing = new Set(d.report.recipients || []);
      // Populate from existing recipients on first load
      const checks = {};
      (d.report.recipients || []).forEach((e) => { checks[e] = true; });
      setMemberChecks((prev) => Object.keys(prev).length ? prev : checks);
      setAdditional((a) => a || "");
    }).catch(() => {});
    // Recipients suggestion
    if (canManage) {
      portalApi.reportRecipients(slug, report.kind).then((d) => {
        if (!alive) return;
        setMembers(d.members || []);
        setExtras(d.extras || []);
        setConfigured(d.configured || []);
        // Pre-tick defaults only on first load (not on re-open after manual edits)
        setMemberChecks((prev) => {
          if (Object.keys(prev).length) return prev;
          const c = {};
          const cfg = d.configured || [];
          if (report.automation && cfg.length && d.use_configured_default) {
            // Automation drafts pre-select the EXACT saved project config —
            // never the globally flagged users (no accidental extras).
            cfg.forEach((x) => { c[x.email] = true; });
            return c;
          }
          (d.members || []).forEach((m) => { if (m.default_checked) c[m.email] = true; });
          (d.extras || []).forEach((e) => { c[e.email] = true; });
          return c;
        });
      }).catch(() => {});
    }
    return () => { alive = false; };
  }, [slug, reportId, canManage, report.kind]);

  const refresh = async () => {
    try {
      const d = await portalApi.getReport(slug, reportId);
      setR(d.report); setResponses(d.responses || []);
    } catch { /* ignore */ }
  };

  const collectRecipients = () => {
    const out = new Set();
    Object.entries(memberChecks).forEach(([email, v]) => { if (v) out.add(email); });
    additional.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean).forEach((e) => out.add(e));
    return Array.from(out);
  };

  const toggle = (email) => setMemberChecks((m) => ({ ...m, [email]: !m[email] }));

  const send = async () => {
    setBusy(true); setMsg("");
    try {
      const list = collectRecipients();
      if (!list.length) { setMsg("Add at least one recipient (tick a member or type extra emails)."); setBusy(false); return; }
      await portalApi.sendReport(slug, r.id, list, {
        response_type: respType,
        required_from: respType === "none" ? null : requiredFrom,
      });
      setMsg("Report sent successfully.");
      await refresh();
      onChanged && onChanged();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm("Discard this draft report? This cannot be undone.")) return;
    setBusy(true); setMsg("");
    try {
      await portalApi.discardReport(slug, r.id);
      setMsg("Draft discarded.");
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) { setMsg(e.message); setBusy(false); }
  };

  const testSend = async () => {
    setBusy(true); setMsg("");
    try {
      const res = await portalApi.testSendReport(slug, r.id);
      setMsg(`Test sent to ${res.recipient}. Client recipients NOT contacted.`);
    } catch (e) { setMsg(`Test send failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const submitResponse = async (e) => {
    e.preventDefault();
    if (!responseText.trim()) return;
    setBusy(true); setMsg("");
    try {
      await portalApi.respondReport(slug, r.id, responseText);
      setResponseText("");
      await refresh();
      onChanged && onChanged();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const status = REPORT_STATUS[r.status] || { label: r.status, cls: "bg-[#7A6B5F] text-white" };
  const audienceLabel = r.audience === "client" ? "Client" : r.audience === "internal" ? "Internal" : null;
  const recipientsPreview = collectRecipients();
  return (
    <div className="fixed inset-0 z-[55] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div
        data-testid="portal-report-detail-modal"
        className="bg-white w-full max-w-[860px] border border-[#6E1F2C]/20 my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-7 py-5 border-b border-[#6E1F2C]/10 flex items-start justify-between gap-3 bg-gradient-to-r from-white to-[#FAF5EC]">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] font-medium">
                {r.kind === "daily" ? "Daily" : "Weekly"}
              </span>
              <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${status.cls}`}>{status.label}</span>
              {r.automation && (
                <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#D4A963]/20 text-[#A6822F] border border-[#D4A963]/50 inline-flex items-center gap-1">
                  <Clock size={9} /> Auto-generated
                </span>
              )}
              {audienceLabel && (
                <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${audienceLabel === "Client" ? "bg-[#D4A963] text-[#1C110C]" : "bg-[#1C110C] text-white"}`}>
                  {audienceLabel} view
                </span>
              )}
              {r.include_financials && (
                <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#3F6E2C]/15 text-[#3F6E2C] border border-[#3F6E2C]/30 inline-flex items-center gap-1">
                  🔒 Financials
                </span>
              )}
            </div>
            <h3 className="font-serif-display text-2xl text-[#1C110C]">{r.title}</h3>
            <p className="text-[11px] text-[#7A6B5F] mt-1">
              {r.kind === "weekly" && r.period_end ? `${r.period_start} → ${r.period_end}` : r.period_start}
              {r.sent_at && ` · Sent ${new Date(r.sent_at).toLocaleString()}`}
              {r.approved_at && r.status !== "sent" && ` · Approved ${new Date(r.approved_at).toLocaleString()}`}
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="portal-report-detail-close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={20} /></button>
        </div>

        <div className="px-7 py-5 space-y-5">
          {r.status === "draft" && r.automation && canManage && (
            <div
              data-testid="portal-report-detail-awaiting-banner"
              className="bg-[#D4A963]/10 border-l-4 border-[#A6822F] p-4 flex items-start gap-3"
            >
              <ShieldCheck size={18} className="text-[#A6822F] mt-0.5 shrink-0" />
              <div className="text-[12px] text-[#3E2A22] leading-relaxed">
                <p className="font-medium text-[#1C110C] mb-0.5">This auto-generated draft is ready to send.</p>
                Review the content below, confirm recipients, then hit <span className="font-semibold text-[#3F6E2C]">Send</span> to email it.
                Use <span className="font-semibold text-[#A3141A]">Discard</span> for unwanted/test runs.
              </div>
            </div>
          )}
          {r.body_summary && (
            <p className="text-sm text-[#3E2A22] italic border-l-2 border-[#D4A963] pl-3">{r.body_summary}</p>
          )}
          {r.body_html && (
            <div
              className="border border-[#6E1F2C]/10 bg-[#FAF5EC]/40 px-4 py-4 max-h-[400px] overflow-y-auto"
              data-testid="portal-report-detail-body"
              // Server-generated HTML still goes through DOMPurify as a
              // defence-in-depth measure: the report body interpolates
              // user-typed fields (work_completed, notes, recipient names).
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(r.body_html, {
                  ADD_ATTR: ["target", "dir"],
                }),
              }}
            />
          )}
          {pdfLinks.pdf_url && (
            <div className="flex flex-wrap items-center gap-3" data-testid="portal-report-pdf-actions">
              <a
                href={pdfLinks.pdf_url}
                target="_blank" rel="noopener noreferrer"
                data-testid="portal-report-download-pdf"
                className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
              >
                <Download size={13} /> Download PDF
              </a>
              {pdfLinks.pdf_share_url && (
                <button
                  type="button"
                  data-testid="portal-report-share-pdf"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(pdfLinks.pdf_share_url);
                      setShareCopied(true);
                      setTimeout(() => setShareCopied(false), 2000);
                    } catch {
                      window.prompt("Copy this signed share link:", pdfLinks.pdf_share_url);
                    }
                  }}
                  className="inline-flex items-center gap-2 border border-[#6E1F2C]/30 text-[#6E1F2C] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC]"
                >
                  {shareCopied ? "Link copied ✓" : "Copy signed share link"}
                </button>
              )}
            </div>
          )}
          {canManage && (
            <div className="space-y-3 pt-4 border-t border-[#6E1F2C]/15">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium inline-flex items-center gap-1.5">
                <Mail size={11} /> Recipients ({recipientsPreview.length})
              </p>
              {recipientsPreview.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="portal-report-selected-recipients">
                  {recipientsPreview.map((e) => (
                    <span key={e} className="text-[10px] bg-[#1C110C] text-[#FAF5EC] px-2 py-0.5">{e}</span>
                  ))}
                </div>
              )}
              {members.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="portal-report-detail-members">
                  {members.map((m) => (
                    <label key={m.email} className={`flex items-start gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                      memberChecks[m.email] ? "border-[#6E1F2C] bg-[#FAF5EC]" : "border-[#6E1F2C]/15 hover:border-[#6E1F2C]/30 bg-white"
                    }`}>
                      <input
                        type="checkbox"
                        checked={!!memberChecks[m.email]}
                        onChange={() => toggle(m.email)}
                        data-testid={`portal-report-recipient-${m.user_id}`}
                        className="mt-0.5 accent-[#6E1F2C]"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-[#1C110C] font-medium truncate">{m.name}</p>
                        <p className="text-[10px] text-[#7A6B5F] truncate">{m.email} · {m.role || "team"}</p>
                      </div>
                      {m.default_checked && (
                        <span className="text-[9px] tracking-[0.18em] uppercase text-[#A6822F]" title="Set as default recipient">★</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              {extras.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {extras.map((e) => (
                    <label key={e.email} className={`text-[10px] tracking-wide px-2 py-1 border cursor-pointer ${
                      memberChecks[e.email] ? "bg-[#FAF5EC] border-[#6E1F2C]" : "bg-white border-[#6E1F2C]/15"
                    }`}>
                      <input
                        type="checkbox"
                        checked={!!memberChecks[e.email]}
                        onChange={() => toggle(e.email)}
                        className="mr-1 accent-[#6E1F2C]"
                      />
                      {e.email} <span className="text-[#7A6B5F]">· {e.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {configured.length > 0 && (
                <div data-testid="portal-report-configured-recipients">
                  <p className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1">Configured automation recipients</p>
                  <div className="flex flex-wrap gap-1.5">
                    {configured.map((c) => (
                      <label key={`cfg-${c.email}`} className={`text-[10px] tracking-wide px-2 py-1 border cursor-pointer ${
                        memberChecks[c.email] ? "bg-[#FAF5EC] border-[#6E1F2C]" : "bg-white border-[#6E1F2C]/15"
                      }`}>
                        <input
                          type="checkbox"
                          checked={!!memberChecks[c.email]}
                          onChange={() => toggle(c.email)}
                          data-testid={`portal-report-configured-${c.email}`}
                          className="mr-1 accent-[#6E1F2C]"
                        />
                        {c.email}
                        {!c.is_portal_user && <span className="text-[#A6822F]"> · external</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Additional emails (comma / space separated)</label>
                <input
                  data-testid="portal-report-detail-additional"
                  value={additional}
                  onChange={(e) => setAdditional(e.target.value)}
                  placeholder="extra-client@example.com, partner@firm.ae"
                  className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-2.5 text-[#1C110C] text-sm focus:outline-none focus:border-[#6E1F2C]"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Approval / response</label>
                  <select
                    data-testid="portal-report-response-type"
                    value={respType}
                    onChange={(e) => setRespType(e.target.value)}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2.5 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                  >
                    <option value="none">Information only · No approval required</option>
                    <option value="response_required">Response required</option>
                    <option value="approval_required">Approval required</option>
                  </select>
                </div>
                {respType !== "none" && (
                  <div>
                    <label className="block text-[10px] tracking-[0.28em] uppercase text-[#A6822F] mb-1">Required from</label>
                    <select
                      data-testid="portal-report-required-from"
                      value={requiredFrom}
                      onChange={(e) => setRequiredFrom(e.target.value)}
                      className="w-full bg-[#FAF5EC] border border-[#D4A963]/50 px-3 py-2.5 text-sm text-[#1C110C] focus:outline-none focus:border-[#A6822F]"
                    >
                      <option value="client">Client</option>
                      <option value="project_manager">Project Manager</option>
                      <option value="designer">Designer</option>
                      <option value="contractor_side">Contractor side</option>
                      <option value="subcontractor">Subcontractor</option>
                      <option value="other">Other selected recipients</option>
                    </select>
                  </div>
                )}
              </div>
              {respType === "none" ? (
                <p className="text-[10px] text-[#7A6B5F] italic">
                  This report will be sent as information only — it will NOT appear in Pending Approvals or Awaiting Reply.
                </p>
              ) : (
                <p className="text-[10px] text-[#A6822F] italic">
                  This report will be tracked as {respType === "approval_required" ? "Awaiting Approval" : "Awaiting Reply"} until a response is posted.
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={testSend}
                  disabled={busy}
                  data-testid="portal-report-detail-test-send"
                  className="inline-flex items-center gap-2 border border-[#D4A963] text-[#A6822F] hover:bg-[#D4A963] hover:text-[#1C110C] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60"
                  title="Send a preview copy only to your email — no client recipients."
                >
                  <Eye size={12} /> Test send to me
                </button>
                <button
                  type="button"
                  onClick={send}
                  disabled={busy || !recipientsPreview.length}
                  data-testid="portal-report-detail-send"
                  className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-6 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60 shadow-[0_4px_12px_-4px_rgba(110,31,44,0.5)]"
                >
                  <Send size={12} /> {busy ? "Sending…" : r.status === "sent" ? "Resend" : "Send"}
                </button>
                {r.status === "draft" && (
                  <button
                    type="button"
                    onClick={discard}
                    disabled={busy}
                    data-testid="portal-report-detail-discard"
                    className="inline-flex items-center gap-2 border border-[#A3141A] text-[#A3141A] hover:bg-[#A3141A] hover:text-white px-3 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60 ml-auto"
                  >
                    <Trash2 size={11} /> Discard draft
                  </button>
                )}
                {msg && <p className="text-[11px] text-[#6E1F2C] ml-1" data-testid="portal-report-detail-msg">{msg}</p>}
              </div>
            </div>
          )}
          <div className="pt-4 border-t border-[#6E1F2C]/15">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-3">
              Responses{responses.length ? ` (${responses.length})` : ""}
            </p>
            {responses.length === 0 && <p className="text-xs text-[#7A6B5F] italic mb-3">No responses yet.</p>}
            <ul className="space-y-3 mb-4">
              {responses.map((rr) => (
                <li key={rr.id} className="bg-[#FAF5EC] border-l-2 border-[#D4A963] px-4 py-3">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
                    {rr.author?.name || "—"} · {new Date(rr.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm text-[#1C110C] mt-1 whitespace-pre-wrap">{rr.body}</p>
                </li>
              ))}
            </ul>
            <form onSubmit={submitResponse} className="flex gap-2 flex-wrap">
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Reply to this report…"
                rows={2}
                className="flex-1 min-w-[200px] bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C]"
                data-testid="portal-report-detail-response"
              />
              <button type="submit" disabled={busy || !responseText.trim()} className="bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-60">
                <Send size={12} className="inline mr-1" /> Reply
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReportsTab({ slug, canManage, autoOpenReportId, isAdmin = false }) {
  const { t } = usePortalLang();
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [genOpen, setGenOpen] = useState(null); // {kind: "daily"|"weekly"} | null
  const [genBusy, setGenBusy] = useState(false);
  const [genAudience, setGenAudience] = useState("client");
  const [genIncludeFin, setGenIncludeFin] = useState(false);
  const [genLanguage, setGenLanguage] = useState("auto");
  const DEFAULT_SECTIONS = {
    financial_snapshot: false, payment_milestone_reminder: false,
    upcoming_payment_due: false, payment_milestones: false, next_stage: false,
  };
  const WEEKLY_SUGGESTED_SECTIONS = {
    ...DEFAULT_SECTIONS, financial_snapshot: true, upcoming_payment_due: true, next_stage: true,
  };
  const [genSections, setGenSections] = useState(DEFAULT_SECTIONS);
  const [genFinNote, setGenFinNote] = useState("");
  const [msg, setMsg] = useState("");
  const [autoOpened, setAutoOpened] = useState(false);

  const reload = useCallback(async () => {
    try {
      const d = await portalApi.listReports(slug);
      setReports(d.reports || []);
      return d.reports || [];
    } catch { return []; }
  }, [slug]);

  useEffect(() => { reload(); }, [reload]);

  // Deep-link from email: open the requested report detail modal once the
  // reports list has loaded. Only fires once per mount.
  useEffect(() => {
    if (!autoOpenReportId || autoOpened || !reports.length) return;
    const target = reports.find((r) => r.id === autoOpenReportId);
    if (target) {
      setSelected(target);
      setAutoOpened(true);
    }
  }, [autoOpenReportId, autoOpened, reports]);

  const generate = async (kind, audience) => {
    setGenBusy(true); setMsg("");
    try {
      const note = genFinNote.trim();
      const anySection = Object.values(genSections).some(Boolean) || note;
      const d = await portalApi.generateReportV3(slug, {
        kind, audience,
        include_financials: audience === "internal" && genIncludeFin,
        language: genLanguage,
        sections: anySection ? { ...genSections, financial_note: note || null } : undefined,
      });
      await reload();
      setGenOpen(null);
      setSelected(d.report);
    } catch (e) { setMsg(e.message); }
    finally { setGenBusy(false); }
  };

  const daily = reports.filter((r) => r.kind === "daily");
  const weekly = reports.filter((r) => r.kind === "weekly");

  return (
    <div data-testid="portal-reports-tab" className="space-y-7">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">{t("rep.title")}</p>
          <h2 className="font-serif-display text-2xl text-[#1C110C]">{t("rep.headline", "Daily & weekly reports")}</h2>
        </div>
        {canManage && (
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => { setGenOpen({ kind: "daily" }); setGenAudience("client"); setGenIncludeFin(false); setGenSections(DEFAULT_SECTIONS); setGenFinNote(""); }}              data-testid="portal-report-generate-daily"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
            >
              <FileText size={12} /> {t("rep.generateDialog.title", null, { kind: t("rep.kind.daily") })}
            </button>
            <button
              type="button"
              onClick={() => { setGenOpen({ kind: "weekly" }); setGenAudience("client"); setGenIncludeFin(false); setGenSections(WEEKLY_SUGGESTED_SECTIONS); setGenFinNote(""); }}
              data-testid="portal-report-generate-weekly"
              className="inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC]"
            >
              <FileText size={12} /> {t("rep.generateDialog.title", null, { kind: t("rep.kind.weekly") })}
            </button>
          </div>
        )}
      </div>
      {msg && <p className="text-[11px] text-[#A3141A]">{msg}</p>}
      {msg && <p className="text-[11px] text-[#6E1F2C]">{msg}</p>}

      {canManage && <AutomationCard slug={slug} isAdmin={isAdmin} />}

      {reports.length === 0 ? (
        <div data-testid="portal-reports-empty" className="bg-white border border-dashed border-[#6E1F2C]/25 p-12 text-center">
          <FileText className="mx-auto text-[#D4A963] mb-3" size={36} />
          <p className="text-sm text-[#3E2A22]">{t("rep.empty")}</p>
        </div>
      ) : (
        <>
          {daily.length > 0 && (
            <section>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-3">Daily reports · {daily.length}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {daily.map((r) => <ReportCard key={r.id} r={r} onOpen={setSelected} canManage={canManage} />)}
              </div>
            </section>
          )}
          {weekly.length > 0 && (
            <section>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-3">Weekly reports · {weekly.length}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {weekly.map((r) => <ReportCard key={r.id} r={r} onOpen={setSelected} canManage={canManage} />)}
              </div>
            </section>
          )}
        </>
      )}

      {selected && (
        <ReportDetailModal
          slug={slug}
          report={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onChanged={reload}
        />
      )}

      {genOpen && (
        <div className="fixed inset-0 z-[55] bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            data-testid="portal-report-generate-modal"
            className="bg-white w-full max-w-[560px] border border-[#6E1F2C]/20 max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Generate</p>
                <h3 className="font-serif-display text-xl text-[#1C110C]">
                  {genOpen.kind === "daily" ? "Daily report" : "Weekly report"}
                </h3>
              </div>
              <button type="button" onClick={() => setGenOpen(null)} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#7A6B5F] mb-2">Audience</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "client", label: "Client view", hint: "Hides internal notes & internal-only updates" },
                    { v: "internal", label: "Internal view", hint: "Full report with internal notes" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setGenAudience(opt.v)}
                      data-testid={`portal-report-gen-audience-${opt.v}`}
                      className={`text-left px-4 py-3 border transition-colors ${
                        genAudience === opt.v
                          ? "border-[#6E1F2C] bg-[#FAF5EC]"
                          : "border-[#6E1F2C]/15 hover:border-[#6E1F2C]/40"
                      }`}
                    >
                      <p className="text-sm text-[#1C110C] font-medium">{opt.label}</p>
                      <p className="text-[10px] text-[#7A6B5F] mt-1">{opt.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-[#3E2A22] italic border-l-2 border-[#D4A963] pl-3">
                The report will auto-aggregate site updates, Gantt activities, photos, comments and pending client actions
                for {genOpen.kind === "daily" ? "today" : "the current week (Mon → Sun)"}.
              </p>
              <div className="border border-[#D4A963]/40 bg-[#FAF5EC]/60 px-3 py-3">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#A6822F] mb-0.5">Optional sections</p>
                <p className="text-[10px] text-[#7A6B5F] mb-2.5 leading-snug">
                  Pick what extra information this report should show. Financial sections are only delivered to
                  recipients with financial permission — others receive the same report without them.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {[
                    { k: "financial_snapshot", label: "Financial snapshot", hint: "Short summary: value, invoiced, received, balance", fin: true },
                    { k: "payment_milestone_reminder", label: "Payment milestone reminder", hint: "Narrative: next milestone + payment falling due", fin: true },
                    { k: "upcoming_payment_due", label: "Upcoming payment due", hint: "Next payment amount, date & status", fin: true },
                    { k: "payment_milestones", label: "Payment milestones", hint: "Full payment schedule table", fin: true },
                    { k: "next_stage", label: "Next stage / upcoming works", hint: "Upcoming milestones + planned works", fin: false },
                  ].map((s) => (
                    <label key={s.k} className="flex items-start gap-2 cursor-pointer px-2 py-1.5 hover:bg-white transition-colors">
                      <input
                        type="checkbox"
                        data-testid={`portal-report-section-${s.k.replace(/_/g, "-")}`}
                        className="accent-[#6E1F2C] mt-0.5"
                        checked={!!genSections[s.k]}
                        onChange={(e) => setGenSections((p) => ({ ...p, [s.k]: e.target.checked }))}
                      />
                      <span>
                        <span className="text-[12px] text-[#1C110C] font-medium">
                          {s.fin ? "🔒 " : ""}{s.label}
                        </span>
                        <p className="text-[9px] text-[#7A6B5F] leading-snug">{s.hint}</p>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="mt-2">
                  <label className="block text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1">🔒 Financial note (optional)</label>
                  <input
                    data-testid="portal-report-section-financial-note"
                    value={genFinNote}
                    onChange={(e) => setGenFinNote(e.target.value)}
                    maxLength={1500}
                    placeholder="e.g. Kindly arrange the P2 payment before Thursday to avoid material delays."
                    className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                  />
                </div>
              </div>
              <label
                className={`flex items-start gap-2 cursor-pointer border px-3 py-2.5 transition-colors ${
                  genAudience === "internal"
                    ? "border-[#A6822F]/40 bg-[#FAF5EC] hover:border-[#A6822F]"
                    : "border-[#7A6B5F]/20 opacity-50 cursor-not-allowed"
                }`}
                title={genAudience === "internal" ? "" : "Switch to Internal view to enable the full financial pack"}
              >
                <input
                  type="checkbox"
                  data-testid="portal-report-include-financials"
                  className="accent-[#6E1F2C] mt-0.5"
                  checked={genAudience === "internal" && genIncludeFin}
                  disabled={genAudience !== "internal"}
                  onChange={(e) => setGenIncludeFin(e.target.checked)}
                />
                <span>
                  <span className="text-[12px] text-[#1C110C] font-medium inline-flex items-center gap-1.5">
                    🔒 Full internal financial pack
                    <span className="text-[8px] tracking-[0.22em] uppercase text-[#A6822F]">Admin-only</span>
                  </span>
                  <p className="text-[10px] text-[#7A6B5F] mt-0.5">
                    Complete block: full value summary, payment milestones, stage breakdown and external supply packages.
                    Internal reports only — for client reports use the Optional sections above.
                  </p>
                </span>
              </label>
              <div className="border border-[#6E1F2C]/15 px-3 py-2.5 bg-white">
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1.5">
                  Report language
                </label>
                <select
                  data-testid="portal-report-language"
                  value={genLanguage}
                  onChange={(e) => setGenLanguage(e.target.value)}
                  className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                >
                  <option value="auto">
                    Auto · Same as recipient&rsquo;s preferred language (recommended for client emails)
                  </option>
                  <option value="en">English</option>
                  <option value="ko">한국어 (Korean)</option>
                  <option value="ar">العربية (Arabic)</option>
                  <option value="zh">中文 (Mandarin)</option>
                </select>
                <p className="text-[10px] text-[#7A6B5F] mt-1 leading-snug">
                  Headings, status labels and section titles are translated. User-typed content (work-completed notes,
                  comments, file names) is kept verbatim.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setGenOpen(null)}
                  className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] hover:text-[#1C110C] px-4 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => generate(genOpen.kind, genAudience)}
                  disabled={genBusy}
                  data-testid="portal-report-gen-submit"
                  className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60"
                >
                  <FileText size={12} /> {genBusy ? "Generating…" : "Generate draft"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Mobile quick actions bar ---------- */

export function MobileQuickActions({ items }) {
  return (
    <div
      data-testid="portal-mobile-quick-actions"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#6E1F2C]/20 shadow-[0_-10px_25px_-15px_rgba(110,31,44,0.25)]"
    >
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-px bg-[#6E1F2C]/10">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button
              key={it.key}
              type="button"
              onClick={it.onClick}
              data-testid={`portal-quick-${it.key}`}
              className="bg-white hover:bg-[#FAF5EC] active:bg-[#D4A963]/20 transition-colors px-2 py-2.5 flex flex-col items-center justify-center gap-1 text-[#6E1F2C]"
            >
              <Icon size={18} />
              <span className="text-[9px] tracking-[0.16em] uppercase text-[#1C110C] leading-tight text-center">
                {it.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
